import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Table, Button, Alert, Form, Card, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchCategories,
  deleteCategory,
  toggleCategory,
  CONTENT_TYPES,
  type ContentType,
} from '../redux/slices/categoriesSlice';

const Categories: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items: categories, loading, error } = useAppSelector(s => s.categories);
  const [filterType, setFilterType]   = useState<ContentType | ''>('');
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('');
  const [localError, setLocalError]   = useState('');

  useEffect(() => {
    dispatch(
      fetchCategories({
        applies_to: filterType || undefined,
        active: filterActive === '' ? undefined : filterActive === 'true',
      })
    );
  }, [dispatch, filterType, filterActive]);

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete category "${name}"? This cannot be undone.`)) return;
    const result = await dispatch(deleteCategory(id));
    if (deleteCategory.rejected.match(result)) {
      setLocalError(result.payload as string);
    }
  };

  const handleToggle = async (id: string) => {
    const result = await dispatch(toggleCategory(id));
    if (toggleCategory.rejected.match(result)) {
      setLocalError(result.payload as string);
    }
  };

  const displayError = localError || error;

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 style={{ color: '#00579a', fontWeight: 600, fontSize: '28px', margin: 0 }}>
              Categories
            </h1>
            <p style={{ color: '#8c8c8c', margin: '4px 0 0', fontSize: '14px' }}>
              Manage categories used across Bids, Blogs, Vacancies, Bursaries, Projects and Gallery
            </p>
          </div>
          <Link
            to="/categories/new"
            className="btn"
            style={{
              backgroundColor: '#00579a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontWeight: 500,
              textDecoration: 'none',
              boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
            }}
          >
            + New Category
          </Link>
        </div>

        {/* Error */}
        {displayError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setLocalError('')}
            style={{ borderRadius: '6px', border: '1px solid #ffccc7', backgroundColor: '#fff2f0' }}
          >
            {displayError}
          </Alert>
        )}

        {/* Filters */}
        <Card style={{ border: 'none', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
          <Card.Body style={{ padding: '16px 20px' }}>
            <Row className="g-3 align-items-end">
              <Col xs={12} sm={5} md={4}>
                <Form.Label style={{ fontWeight: 500, color: '#262626', fontSize: '13px', marginBottom: '6px' }}>
                  Filter by Content Type
                </Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={e => setFilterType(e.target.value as ContentType | '')}
                  style={{ borderRadius: '6px', border: '1px solid #d9d9d9', fontSize: '14px' }}
                >
                  <option value="">All Types</option>
                  {CONTENT_TYPES.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={4} md={3}>
                <Form.Label style={{ fontWeight: 500, color: '#262626', fontSize: '13px', marginBottom: '6px' }}>
                  Status
                </Form.Label>
                <Form.Select
                  value={filterActive}
                  onChange={e => setFilterActive(e.target.value as '' | 'true' | 'false')}
                  style={{ borderRadius: '6px', border: '1px solid #d9d9d9', fontSize: '14px' }}
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Form.Select>
              </Col>
              <Col xs="auto">
                <Button
                  variant="link"
                  onClick={() => { setFilterType(''); setFilterActive(''); }}
                  style={{ color: '#8c8c8c', padding: '6px 0', fontSize: '13px' }}
                >
                  Clear filters
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card style={{ border: 'none', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <Card.Body style={{ padding: 0 }}>
            {loading ? (
              <div className="text-center" style={{ padding: '60px 0' }}>
                <Spinner animation="border" style={{ color: '#00579a', width: '40px', height: '40px' }} />
                <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading categories...</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table hover style={{ margin: 0 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th style={th}>Name</th>
                      <th style={th}>Slug</th>
                      <th style={th}>Applies To</th>
                      <th style={th}>Color</th>
                      <th style={th}>Status</th>
                      <th style={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center" style={{ padding: '60px 20px', color: '#8c8c8c', fontSize: '15px' }}>
                          No categories found. Click "+ New Category" to create one.
                        </td>
                      </tr>
                    ) : (
                      categories.map(cat => (
                        <tr key={cat.id}>
                          {/* Name */}
                          <td style={{ ...td, fontWeight: 600, color: '#262626' }}>
                            <div className="d-flex align-items-center gap-2">
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: cat.color || '#00579a',
                                  flexShrink: 0,
                                }}
                              />
                              {cat.name}
                            </div>
                            {cat.description && (
                              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px', fontWeight: 400 }}>
                                {cat.description}
                              </div>
                            )}
                          </td>

                          {/* Slug */}
                          <td style={td}>
                            <code style={{ fontSize: '12px', backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '3px' }}>
                              {cat.slug}
                            </code>
                          </td>

                          {/* Applies To */}
                          <td style={td}>
                            <div className="d-flex flex-wrap gap-1">
                              {cat.applies_to.split(',').map(type => {
                                const label = CONTENT_TYPES.find(c => c.value === type.trim())?.label || type;
                                return (
                                  <Badge
                                    key={type}
                                    style={{
                                      backgroundColor: '#e6f4ff',
                                      color: '#0050b3',
                                      fontWeight: 500,
                                      padding: '3px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                    }}
                                  >
                                    {label}
                                  </Badge>
                                );
                              })}
                            </div>
                          </td>

                          {/* Color */}
                          <td style={td}>
                            <div className="d-flex align-items-center gap-2">
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '4px',
                                  backgroundColor: cat.color || '#00579a',
                                  border: '1px solid rgba(0,0,0,0.1)',
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ fontSize: '12px', color: '#595959' }}>{cat.color}</span>
                            </div>
                          </td>

                          {/* Status */}
                          <td style={td}>
                            <Badge
                              style={{
                                backgroundColor: cat.is_active ? '#f6ffed' : '#fff1f0',
                                color: cat.is_active ? '#389e0d' : '#cf1322',
                                fontWeight: 500,
                                padding: '4px 10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              {cat.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>

                          {/* Actions */}
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <Link
                              to={`/categories/edit/${cat.id}`}
                              className="btn btn-sm me-2"
                              style={editBtn}
                            >
                              Edit
                            </Link>
                            <Button
                              size="sm"
                              onClick={() => handleToggle(cat.id)}
                              className="me-2"
                              style={cat.is_active ? deactivateBtn : activateBtn}
                            >
                              {cat.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDelete(cat.id, cat.name)}
                              style={deleteBtn}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────

const th: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
};

const editBtn: React.CSSProperties = {
  backgroundColor: 'white',
  color: '#00579a',
  border: '1px solid #00579a',
  borderRadius: '4px',
  padding: '4px 12px',
  fontSize: '12px',
  fontWeight: 500,
  textDecoration: 'none',
};

const deactivateBtn: React.CSSProperties = {
  backgroundColor: 'white',
  color: '#d46b08',
  border: '1px solid #d46b08',
  borderRadius: '4px',
  padding: '4px 12px',
  fontSize: '12px',
  fontWeight: 500,
};

const activateBtn: React.CSSProperties = {
  backgroundColor: 'white',
  color: '#389e0d',
  border: '1px solid #389e0d',
  borderRadius: '4px',
  padding: '4px 12px',
  fontSize: '12px',
  fontWeight: 500,
};

const deleteBtn: React.CSSProperties = {
  backgroundColor: 'white',
  color: '#ff4d4f',
  border: '1px solid #ff4d4f',
  borderRadius: '4px',
  padding: '4px 12px',
  fontSize: '12px',
  fontWeight: 500,
};

export default Categories;